import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import { AppConstant } from '../common/app.constant';
import { IAuthorityHistoryCommonChange } from '../models/authority-history-common.interface';
import { IAuthorityHistoryModel } from '../models/authority-history.interface';
import { IAuthorityModificationDisplay } from '../models/authority-modification-display.interface';
import { AuthorityPoint } from '../models/authoritypoint';
import { Comment } from '../models/comment';
import { CommentType } from '../models/commenttype.enum';
import { CrossOver } from '../models/crossover';
import { Direction } from '../models/direction.enum';
import { HttpResponse } from '../models/http';
import { LineItem } from '../models/lineitem';
import { Source } from '../models/source.enum';
import { Status } from '../models/status.enum';
import { TrackAuthority } from '../models/trackauthority';
import { TTPSharedDetail } from '../models/ttp-shared-list';
import { IUserConfirmation, IUserConfirmationLabel } from '../models/userconfirmation';
import { DataService } from '../services/data.service';
import { ATMEventData, EventBusService, EventType } from '../services/eventbus.service';
import { UtilService } from '../services/util.service';
import { DataShareService } from './data-share.service';
import { ChangeRequestType } from '../models/change-request-type.enum';
import { ProxyUser } from '../models/proxyuser';
import { User } from '@csx/security';

@Injectable()
export class AffirmationService {
  public initialAuthority: TrackAuthority;
  private currentAuthority: TrackAuthority;
  public authorityHistoryCount = 0;
  public authorityHistories: IAuthorityModificationDisplay[] = [];
  public authorityHistoryModels: IAuthorityHistoryModel[] = [];

  constructor(private dataService: DataService, private dataShareService: DataShareService, private eventBus: EventBusService, private user: User, private proxy: ProxyUser) {}

  getAffirmations(trackAuthority: TrackAuthority, action: Status | Source): Promise<IUserConfirmation[]> {
    return new Promise<IUserConfirmation[]>((resolve, reject) => {
      if (action === Source.TRANSFER_TO_PAPER) {
        this.initialAuthority = trackAuthority;
        this.currentAuthority = trackAuthority;

        if (trackAuthority.trackAuthorityHistoryModels && trackAuthority.trackAuthorityHistoryModels.length > 0) {
          this.authorityHistoryModels = trackAuthority.trackAuthorityHistoryModels;
          this.authorityHistoryCount = this.authorityHistoryModels.length > 0 ? this.authorityHistoryModels.length : 0;

          this.authorityHistoryModels.map((authorityHistory: IAuthorityHistoryModel) => {
            this.authorityHistories.push({
              authorityHistoryChange: authorityHistory.authorityInfoChangeModels,
              authoritySnapshot: new TrackAuthority(authorityHistory.authoritySnapshot, (this.proxy.racf || this.user.racf)),
              authorityHistoryCommonChange: this.getAuthorityHistoryCommonChange(authorityHistory)
            });
          });
          this.initialAuthority = this.authorityHistories[this.authorityHistories.length - 1].authoritySnapshot;
        }
        resolve(this.parseAffirmationsOffline(this.initialAuthority, this.currentAuthority));
        //used to clear out old mapings
        this.authorityHistories = [];
        this.authorityHistoryModels = [];
      } else {
        this.dataService
          .getUserConfirmationList(trackAuthority, action)
          .then(response => resolve(response))
          .catch((error: HttpResponse) => {
            reject(error);
            this.eventBus.fireEvent(EventType.NOTIFY_FAILURE_EVENT, new ATMEventData(null, () => error.errorMessage));
          });
      }
    });
  }

  private getAuthorityHistoryCommonChange(authorityHistory: IAuthorityHistoryModel) {
    let commonChangeData: IAuthorityHistoryCommonChange = {
      requestedById: authorityHistory.requestedById,
      changeTimeStamp: authorityHistory.changeTimeStamp,
      dispatcher: authorityHistory.dispatcher,
      requestedByName: authorityHistory.requestedByName,
      changeType: authorityHistory.changeType
    };
    return commonChangeData;
  }

  private parseAffirmationsOffline(trackAuthority: TrackAuthority, currentAuthority: TrackAuthority): Array<IUserConfirmation> {
    let userConfirmationList: Array<IUserConfirmation> = new Array<IUserConfirmation>();
    trackAuthority.lineItems.forEach((lineItem: LineItem) => {
      userConfirmationList.push({
        accepted: false,
        confirmType: 'A',
        title: 'LINE: ' + lineItem.lineNumber,
        userConfirmationLabelList: [
          {
            accepted: false,
            confirmMessage:
              'Operate On: ' + lineItem.operateOn === undefined || lineItem.operateOn === null
                ? 'Operate On: -'
                : 'Operate On: -' + lineItem.operateOn
          },
          { accepted: false, confirmMessage: 'Operating From: ' + this.parseDispatchPointDisplayName(lineItem.fromPoint) },
          { accepted: false, confirmMessage: 'Operating To: ' + this.parseDispatchPointDisplayName(lineItem.toPoint) },
          { accepted: false, confirmMessage: 'Direction: ' + Direction[lineItem.direction] },
          { accepted: false, confirmMessage: 'Time/Date: : ' + this.parseAuthorityTime(trackAuthority) }
        ]
      } as IUserConfirmation);
    });
    trackAuthority.crossOvers.forEach((crossOver: CrossOver) => {
      if (crossOver.cancelledDate === null || crossOver.cancelledDate === undefined) {
        userConfirmationList.push({
          accepted: false,
          confirmType: 'A',
          title: 'LINE: ' + crossOver.lineNumber,
          userConfirmationLabelList: [
            {
              accepted: false,
              confirmMessage:
                'Cross Over At: ' + crossOver.location + ' From Track: ' + crossOver.fromTrack.name + ' To Track: ' + crossOver.toTrack.name
            }
          ]
        } as IUserConfirmation);
      }
    });

    if (
      currentAuthority.modifyReportbyChangeRequest !== undefined &&
      currentAuthority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_USER_APPROVED.toString() &&
      currentAuthority.modifyReportbyChangeRequest.changeRequestLineComment !== undefined
    ) {
      let changeRequestConfirmationLabels: IUserConfirmationLabel[] = [
        {
          accepted: false,
          confirmMessage: currentAuthority.modifyReportbyChangeRequest.changeRequestLineComment
        }
      ];
      userConfirmationList.push({
        accepted: false,
        confirmType: 'A',
        title: 'LINE: 11',
        userConfirmationLabelList: changeRequestConfirmationLabels
      } as IUserConfirmation);
    } else {
      let confirmationLabels: IUserConfirmationLabel[] = trackAuthority.comments
        .filter((comment: Comment) => UtilService.isCommentOfType(comment, [CommentType.LINE11]))
        .map((comment: Comment) => {
          return { accepted: false, confirmMessage: comment.text };
        });
      if (confirmationLabels.length > 0) {
        userConfirmationList.push({
          accepted: false,
          confirmType: 'A',
          title: 'LINE: 11',
          userConfirmationLabelList: confirmationLabels
        } as IUserConfirmation);
      }
    }

    let otherDetailsConfirmationLabels: IUserConfirmationLabel[] = [
      {
        accepted: false,
        confirmMessage: 'Entry Location: ' + trackAuthority.entryLocation.prefix + ' ' + trackAuthority.entryLocation.milepost
      },
      { accepted: false, confirmMessage: 'Dispatcher Initials: ' + trackAuthority.dispatcher }
    ];

    let privateComment: Comment = UtilService.getFirstCommentOfTypes(trackAuthority.comments, [CommentType.PRIVATE])[0];
    if (privateComment !== undefined) {
      otherDetailsConfirmationLabels.push({ accepted: false, confirmMessage: 'Private Comment: ' + privateComment.text });
    }
    if (currentAuthority.modifyReportbyChangeRequest.authorityChangeRequestStatus === Status.CHANGE_USER_APPROVED.toString()) {
      currentAuthority.modifyReportbyChangeRequest.changeUserAcceptedMessages.forEach(item => {
        otherDetailsConfirmationLabels.push({
          accepted: false,
          confirmMessage: item.text
        });
      });
    }
    userConfirmationList.push({
      accepted: false,
      confirmType: 'A',
      title: 'OTHER DETAILS',
      userConfirmationLabelList: otherDetailsConfirmationLabels
    } as IUserConfirmation);

    if (this.authorityHistoryCount > 0) {
      this.parseHistoryChanges(userConfirmationList, currentAuthority);
    }

    return userConfirmationList;
  }

  private parseHistoryChanges(userConfirmation: Array<IUserConfirmation>, currentAuthority: TrackAuthority) {
    let effectiveDate: String;
    let shared = new Array<IUserConfirmationLabel>();

    let timeExtension = new Array<IUserConfirmationLabel>();
    let changeDirection = new Array<IUserConfirmationLabel>();
    let cancelLine = new Array<IUserConfirmationLabel>();
    let reportBy = new Array<IUserConfirmationLabel>();
    let ttpSharedDetailList: TTPSharedDetail[] = new Array<TTPSharedDetail>();
    let authorityHistoryChange;
    for (let pointer = this.authorityHistories.length - 1; pointer >= 0; pointer--) {
      if (pointer === 0) {
        const dateAffirmation = UtilService.estToLocal(new Date(currentAuthority.effective));
        effectiveDate = dateAffirmation.toLocaleString('en', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        const dateAffirmation = UtilService.estToLocal(new Date(this.authorityHistories[pointer - 1].authoritySnapshot.effective));
        effectiveDate = dateAffirmation.toLocaleString('en', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (this.authorityHistories[pointer].authorityHistoryChange[0].changeType == 'REPORTBY') {
        reportBy.push({
          accepted: false,
          confirmMessage:
            'Employee: ' +
            this.authorityHistories[pointer].authorityHistoryCommonChange.requestedByName +
            ' ' +
            this.authorityHistories[pointer].authorityHistoryChange[0].changeText +
            '\n' +
            'Effective: ' +
            effectiveDate +
            ' Dispatcher: ' +
            this.authorityHistories[pointer].authorityHistoryCommonChange.dispatcher
        });
      }

      if (this.authorityHistories[pointer].authorityHistoryChange[0].changeType == 'MODIFY') {
        for (let change of this.authorityHistories[pointer].authorityHistoryChange) {
          if (change.changeText.toLowerCase().includes('time')) {
            timeExtension.push({
              accepted: false,
              confirmMessage:
                'Time Extended Until: ' +
                change.changeText.substr(23) +
                '\n' +
                'Effective: ' +
                effectiveDate +
                ' Dispatcher: ' +
                this.authorityHistories[pointer].authorityHistoryCommonChange.dispatcher
            });
          } else if (change.changeText.toLowerCase().includes('direction')) {
            changeDirection.push({
              accepted: false,
              confirmMessage:
                change.changeText +
                '\n' +
                'Effective: ' +
                effectiveDate +
                ' Dispatcher: ' +
                this.authorityHistories[pointer].authorityHistoryCommonChange.dispatcher
            });
          } else if (change.changeText.toLowerCase().includes('canceled')) {
            cancelLine.push({
              accepted: false,
              confirmMessage:
                change.changeText +
                '\n' +
                'Effective: ' +
                effectiveDate +
                ' Dispatcher: ' +
                this.authorityHistories[pointer].authorityHistoryCommonChange.dispatcher
            });
          }
        }
      }
      authorityHistoryChange = this.authorityHistories[pointer].authorityHistoryChange[0];
      if (
        authorityHistoryChange.changeType === ChangeRequestType.UNSHARE ||
        authorityHistoryChange.changeType === ChangeRequestType.SHARE
      ) {
        let sharedDetails: TTPSharedDetail = new TTPSharedDetail();
        let authorityHistoryCommonChange = this.authorityHistories[pointer].authorityHistoryCommonChange;
        sharedDetails.changeTextName = authorityHistoryChange.changeText
          .toString()
          .split('-')[1]
          .split('(')[0]
          .trim();
        const dateAffirmationSA = UtilService.estToLocal(
          new Date(this.authorityHistories[pointer].authorityHistoryCommonChange.changeTimeStamp.toString().slice(0, -5))
        );
        const effectiveDateSA = dateAffirmationSA.toLocaleString('en', {
          year: 'numeric',
          month: '2-digit',
          day: 'numeric',
          hour12: false,
          hour: '2-digit',
          minute: '2-digit'
        });
        if (authorityHistoryChange.changeType === ChangeRequestType.UNSHARE) {
          let index = ttpSharedDetailList.map(ttsd => ttsd.changeTextName).lastIndexOf(sharedDetails.changeTextName);
          if (index >= 0) {
            ttpSharedDetailList[index].timeReleased = effectiveDateSA;
          }
        } else if (authorityHistoryChange.changeType === ChangeRequestType.SHARE) {
          sharedDetails.entryLocation = AppConstant.regExp.exec(authorityHistoryChange.changeText)[1];
          sharedDetails.timeAuthorised = effectiveDateSA;
          sharedDetails.name = authorityHistoryCommonChange.requestedByName.toString().trim();
          ttpSharedDetailList.push(sharedDetails);
        }
      }
    }

    if (timeExtension.length != null && timeExtension.length > 0) {
      userConfirmation.push({
        accepted: false,
        confirmType: 'A',
        title: 'TIME EXTENSION',
        userConfirmationLabelList: timeExtension
      } as IUserConfirmation);
    }
    if (changeDirection.length != null && changeDirection.length > 0) {
      userConfirmation.push({
        accepted: false,
        confirmType: 'A',
        title: 'CHANGE DIRECTION',
        userConfirmationLabelList: changeDirection
      } as IUserConfirmation);
    }
    if (cancelLine.length != null && cancelLine.length > 0) {
      userConfirmation.push({
        accepted: false,
        confirmType: 'A',
        title: 'CANCEL LINE',
        userConfirmationLabelList: cancelLine
      } as IUserConfirmation);
    }
    if (reportBy.length != null && reportBy.length > 0) {
      userConfirmation.push({
        accepted: false,
        confirmType: 'A',
        title: 'REPORT BY',
        userConfirmationLabelList: reportBy
      } as IUserConfirmation);
    }
    if (ttpSharedDetailList.length > 0) {
      ttpSharedDetailList.forEach((ttpSharedDetail: TTPSharedDetail) => {
        shared.push({
            accepted: false,
            confirmMessage:
              this.addAffirmationMessage(ttpSharedDetail)
            });
          });
      userConfirmation.push({
        accepted: false,
        confirmType: AppConstant.CONFIRM_TYPE_A,
        title: AppConstant.SHARED_AUTHORITY_TEXT_CAPS,
        userConfirmationLabelList: shared
      } as IUserConfirmation);
    }
  }

  private addAffirmationMessage(ttpSharedDetail: TTPSharedDetail): string {
    let confirmMessage: string =
      'RWIC: ' +
      this.initialAuthority.primaryDetail.eicEmployeeName +
      '\nEmployee: ' +
      this.dataShareService.getNameByFirstAndLastName(ttpSharedDetail.name) +
      '\nInitial Point of Occupancy: ' +
      ttpSharedDetail.entryLocation;
    if (ttpSharedDetail.timeAuthorised) {
      confirmMessage = confirmMessage +
        '\n' +
        ' Time Authorized: ' +
        ttpSharedDetail.timeAuthorised;
    }
    if (ttpSharedDetail.timeReleased) {
      confirmMessage = confirmMessage +
        '\n' +
        ' Time Released: ' +
        ttpSharedDetail.timeReleased;
    }
    return confirmMessage;
  }

  private parseDispatchPointDisplayName(authorityPoint: AuthorityPoint): string {
    let displayName: string = authorityPoint.displayName + ', ';
    if (authorityPoint.location !== undefined && authorityPoint.location.track !== undefined) {
      displayName = displayName + authorityPoint.location.track.name;
    } else {
      displayName = displayName + 'Unknown';
    }
    return displayName;
  }

  private parseAuthorityTime(trackAuthority: TrackAuthority): string {
    let parsedDateTime: string = '';
    if (trackAuthority.effective !== undefined && trackAuthority.effective !== null) {
      const effectiveTimeLocal = UtilService.estToLocal(
        new Date(trackAuthority.effective));
      const formmatedEffectiveTimeLocal = effectiveTimeLocal.toLocaleString('en', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      })
      parsedDateTime = formmatedEffectiveTimeLocal + ' to ';
    }
    const expTimeLocal = UtilService.estToLocal(
      new Date(trackAuthority.expiration));
    const formattedExpTimeLocal = expTimeLocal.toLocaleString('en', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit'
    })
    return parsedDateTime + formattedExpTimeLocal;
  }
}
